export type ReleaseEnvironment = 'production' | 'preview' | 'local'
export type CustomerActionsState = 'enabled' | 'disabled'

export interface ReleaseInfo {
  environment: ReleaseEnvironment
  label: string
  release: string
  releasedAt: string | null
  releasedAtLabel: string
  customerActions: CustomerActionsState
  medicalCustomerActions?: CustomerActionsState
  customerActionsConfigured: boolean
  isProduction: boolean
}

export interface ReleaseInfoInput {
  mode: string
  configuredEnvironment?: string
  releaseSha?: string
  releaseTime?: string
  customerActionsEnabled?: string
  medicalCustomerActionsEnabled?: string
}

function clean(value: string | undefined) {
  const trimmed = value?.trim()
  return trimmed || undefined
}

function resolveEnvironment(mode: string, configuredEnvironment?: string): ReleaseEnvironment {
  if (mode.trim().toLowerCase() === 'development') return 'local'

  switch (clean(configuredEnvironment)?.toLowerCase()) {
    case 'production':
      return 'production'
    case 'local':
    case 'development':
      return 'local'
    case 'preview':
    case 'test':
      return 'preview'
    default:
      return 'preview'
  }
}

function resolveRelease(releaseSha?: string) {
  const value = clean(releaseSha)?.toLowerCase()
  return value && /^[0-9a-f]{7,64}$/.test(value) ? value.slice(0, 12) : 'unstamped build'
}

function resolveReleaseTime(releaseTime?: string) {
  const value = clean(releaseTime)
  if (!value || !/^\d{4}-\d{2}-\d{2}t\d{2}:\d{2}:\d{2}(?:\.\d{3})?z$/i.test(value)) {
    return { releasedAt: null, releasedAtLabel: 'time unstamped' }
  }

  const timestamp = new Date(value)
  if (Number.isNaN(timestamp.getTime())) {
    return { releasedAt: null, releasedAtLabel: 'time unstamped' }
  }

  const releasedAt = timestamp.toISOString()
  const parts = new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  }).formatToParts(timestamp)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find(item => item.type === type)!.value
  return {
    releasedAt,
    releasedAtLabel: `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')} ${part('timeZoneName')}`,
  }
}

export function resolveReleaseInfo(input: ReleaseInfoInput): ReleaseInfo {
  const environment = resolveEnvironment(input.mode, input.configuredEnvironment)
  const releaseTime = resolveReleaseTime(input.releaseTime)
  const configuredActions = clean(input.customerActionsEnabled)?.toLowerCase()
  const customerActionsConfigured = configuredActions === 'true' || configuredActions === 'false'
  const customerActions = configuredActions === 'true' ? 'enabled' : 'disabled'

  return {
    environment,
    label: environment === 'production' ? 'Production' : environment === 'local' ? 'Local development' : 'Test / Preview',
    release: resolveRelease(input.releaseSha),
    ...releaseTime,
    customerActions,
    medicalCustomerActions: clean(input.medicalCustomerActionsEnabled) === 'true' ? 'enabled' : clean(input.medicalCustomerActionsEnabled) === 'false' ? 'disabled' : customerActions,
    customerActionsConfigured,
    isProduction: environment === 'production',
  }
}

export function currentReleaseInfo() {
  return resolveReleaseInfo({
    mode: import.meta.env.MODE,
    configuredEnvironment: import.meta.env.VITE_APP_ENVIRONMENT,
    releaseSha: import.meta.env.VITE_RELEASE_SHA,
    releaseTime: import.meta.env.VITE_RELEASE_TIME,
    customerActionsEnabled: import.meta.env.VITE_CUSTOMER_ACTIONS_ENABLED,
    medicalCustomerActionsEnabled: import.meta.env.VITE_MEDICAL_CUSTOMER_ACTIONS_ENABLED,
  })
}
